if (!customElements.get('ps-media-viewer')) {
  customElements.define('ps-media-viewer', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.ps-viewer__stage');
      this.zoomButton = this.querySelector('[data-zoom]');
      this.slides = [...this.querySelectorAll('[data-viewer-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-viewer-thumb]')];
      this.index = 0;
      this.zoomed = false;
      this.querySelector('[data-close]').addEventListener('click', () => this.hide());
      this.zoomButton.addEventListener('click', () => this.setZoom(!this.zoomed));
      this.thumbs.forEach((button, index) => button.addEventListener('click', () => this.select(index)));
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.hide(); });
      this.dialog.addEventListener('close', () => this.restore());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog && this.backdropDown) this.hide();
      });
      this.dialog.addEventListener('pointerdown', (event) => { this.backdropDown = event.target === this.dialog; });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') {
          const controls = [...this.dialog.querySelectorAll('button, a[href], video[controls], iframe, [tabindex]')]
            .filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
          const first = controls[0];
          const last = controls[controls.length - 1];
          if ((event.shiftKey && event.target === first) || (!event.shiftKey && event.target === last)) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus({ preventScroll: true });
          }
          return;
        }
        if (event.target.closest('video, iframe, model-viewer')) return;
        if (this.zoomed && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
          event.preventDefault();
          this.pan(event.key === 'ArrowLeft' ? 40 : event.key === 'ArrowRight' ? -40 : 0,
            event.key === 'ArrowUp' ? 40 : event.key === 'ArrowDown' ? -40 : 0);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.select((this.index + (event.key === 'ArrowRight' ? 1 : -1) + this.slides.length) % this.slides.length);
        }
      });
      this.stage.addEventListener('pointerdown', (event) => this.pointerDown(event));
      this.stage.addEventListener('pointermove', (event) => {
        if (!this.pointer || this.pointer.id !== event.pointerId || !this.zoomed) return;
        this.pan(event.clientX - this.pointer.x, event.clientY - this.pointer.y);
        this.pointer.x = event.clientX;
        this.pointer.y = event.clientY;
      });
      this.stage.addEventListener('pointerup', (event) => this.pointerUp(event));
      this.stage.addEventListener('pointercancel', () => { this.pointer = null; });
      this.resizeObserver = new ResizeObserver(() => this.setZoom(false));
      this.resizeObserver.observe(this.stage);
    }

    disconnectedCallback() {
      if (this.dialog.open) this.hide();
      this.resizeObserver?.disconnect();
    }

    show(opener) {
      if (this.dialog.open) return;
      this.openedBy = opener;
      this.scrollY = window.scrollY;
      this.bodyStyle = document.body.getAttribute('style');
      window.pauseAllMedia?.();
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === opener.dataset.mediaId);
      this.dialog.showModal();
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      this.select(Math.max(0, index));
      this.querySelector('[data-close]').focus({ preventScroll: true });
    }

    hide() {
      if (!this.dialog.open) return;
      this.pause();
      this.setZoom(false);
      this.dialog.close();
      this.restore();
    }

    restore() {
      if (this.scrollY === undefined) return;
      if (this.bodyStyle === null) document.body.removeAttribute('style');
      else document.body.setAttribute('style', this.bodyStyle);
      const top = this.scrollY;
      this.scrollY = undefined;
      window.scrollTo({ top, behavior: 'instant' });
      this.openedBy?.focus({ preventScroll: true });
    }

    pause() {
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.querySelectorAll('iframe').forEach((frame) => {
        if (frame.classList.contains('js-youtube')) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
        if (frame.classList.contains('js-vimeo')) frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
      });
      this.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause());
    }

    select(index) {
      this.pause();
      this.setZoom(false);
      this.index = index;
      this.slides.forEach((slide, i) => { slide.hidden = i !== index; });
      this.thumbs.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      const slide = this.slides[index];
      this.zoomButton.hidden = slide.dataset.type !== 'image';
      const image = slide.querySelector('img');
      if (image) image.loading = 'eager';
      const thumb = this.thumbs[index];
      const strip = thumb.parentElement;
      strip.scrollTo({ left: thumb.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2, behavior: 'instant' });
      this.querySelector('[data-status]').textContent = `${index + 1} / ${this.slides.length}`;
    }

    setZoom(enabled) {
      this.zoomed = enabled && this.slides[this.index]?.dataset.type === 'image';
      this.x = 0;
      this.y = 0;
      this.pointer = null;
      this.stage.classList.toggle('is-zoomed', this.zoomed);
      this.zoomButton.setAttribute('aria-pressed', String(this.zoomed));
      this.zoomButton.textContent = this.zoomed ? '1×' : '2×';
      this.slides.forEach((slide) => {
        const image = slide.querySelector(':scope > img');
        if (image) image.style.transform = '';
      });
      if (this.zoomed) this.pan(0, 0);
    }

    pan(dx, dy) {
      const image = this.slides[this.index].querySelector(':scope > img');
      if (!image) return;
      const bounds = this.stage.getBoundingClientRect();
      const ratio = (image.naturalWidth || image.width) / (image.naturalHeight || image.height);
      const width = Math.min(image.clientWidth, image.clientHeight * ratio);
      const height = width / ratio;
      const limitX = Math.max(0, (width * 2 - bounds.width) / 2);
      const limitY = Math.max(0, (height * 2 - bounds.height) / 2);
      this.x = Math.max(-limitX, Math.min(limitX, this.x + dx));
      this.y = Math.max(-limitY, Math.min(limitY, this.y + dy));
      image.style.transform = `translate(${this.x}px, ${this.y}px) scale(2)`;
    }

    pointerDown(event) {
      if (event.button !== 0 || this.slides[this.index].dataset.type !== 'image') return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY };
      this.stage.setPointerCapture(event.pointerId);
      event.preventDefault();
    }

    pointerUp(event) {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const dx = event.clientX - this.pointer.startX;
      const dy = event.clientY - this.pointer.startY;
      if (!this.zoomed && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        this.select((this.index + (dx < 0 ? 1 : -1) + this.slides.length) % this.slides.length);
      }
      if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
      this.pointer = null;
    }
  });
}
